import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { today, Modal, useForm } from '../components/helpers';

const EMPTY = { run_date: today(), bulk_part_id: '', finished_good_id: '', cases_produced: '', box_component_id: '', notes: '' };

export default function PackOutRuns() {
  const [runs, setRuns] = useState([]);
  const [bulkParts, setBulkParts] = useState([]);
  const [skus, setSkus] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, set, setForm] = useForm(EMPTY);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    const [{ data: r }, { data: bp }, { data: fg }, { data: comp }] = await Promise.all([
      supabase.from('packout_runs').select('*, bulk_parts(name), finished_goods(sku,name)').order('run_date', { ascending: false }).order('created_at', { ascending: false }),
      supabase.from('bulk_parts').select('*').order('name'),
      supabase.from('finished_goods').select('*').order('sku'),
      supabase.from('components').select('*').eq('category', 'Box').order('name'),
    ]);
    setRuns(r || []);
    setBulkParts(bp || []);
    setSkus(fg || []);
    setBoxes(comp || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  // Build preview when inputs change
  useEffect(() => {
    if (!form.finished_good_id || !form.cases_produced || !form.bulk_part_id) { setPreview(null); return; }
    const sku = skus.find(s => s.id === form.finished_good_id);
    const bulk = bulkParts.find(b => b.id === form.bulk_part_id);
    const box = boxes.find(b => b.id === form.box_component_id);
    if (!sku || !bulk) { setPreview(null); return; }
    const cases = Number(form.cases_produced);
    const unitsPerCase = Number(sku.case_qty) || 1;
    const unitsNeeded = cases * unitsPerCase;
    setPreview({
      unitsNeeded,
      unitsPerCase,
      bulkOnHand: Number(bulk.on_hand),
      bulkShort: unitsNeeded > Number(bulk.on_hand),
      boxesNeeded: cases,
      boxOnHand: box ? Number(box.on_hand) : null,
      boxShort: box ? cases > Number(box.on_hand) : false,
      boxName: box ? box.name : null,
      casesProduced: cases,
    });
  }, [form.finished_good_id, form.cases_produced, form.bulk_part_id, form.box_component_id, skus, bulkParts, boxes]);

  async function save() {
    if (!form.bulk_part_id || !form.finished_good_id || !form.cases_produced) { alert('Bulk part, SKU, and cases produced are required.'); return; }
    const sku = skus.find(s => s.id === form.finished_good_id);
    const bulk = bulkParts.find(b => b.id === form.bulk_part_id);
    if (!sku || !bulk) return;

    const cases = Number(form.cases_produced);
    const unitsPerCase = Number(sku.case_qty) || 1;
    const unitsConsumed = cases * unitsPerCase;

    if (preview?.bulkShort || preview?.boxShort) {
      if (!window.confirm('Some inventory is below what this pack-out requires. Log anyway?')) return;
    }

    setSaving(true);

    // 1. Log pack-out run
    await supabase.from('packout_runs').insert({
      run_date: form.run_date,
      bulk_part_id: form.bulk_part_id,
      bulk_part_name: bulk.name,
      finished_good_id: form.finished_good_id,
      sku: sku.sku,
      cases_produced: cases,
      units_per_case: unitsPerCase,
      units_consumed: unitsConsumed,
      box_component_id: form.box_component_id || null,
      boxes_consumed: cases,
      notes: form.notes.trim() || null,
    });

    // 2. Deduct bulk parts
    await supabase.from('bulk_parts').update({ on_hand: Math.max(0, Number(bulk.on_hand) - unitsConsumed), updated_at: today() }).eq('id', bulk.id);
    await supabase.from('inventory_adjustments').insert({ item_type: 'raw', item_id: bulk.id, item_name: bulk.name, adjustment: -unitsConsumed, reason: `Pack-out: ${cases} cases of ${sku.sku}` });

    // 3. Deduct boxes if selected
    if (form.box_component_id) {
      const box = boxes.find(b => b.id === form.box_component_id);
      if (box) {
        await supabase.from('components').update({ on_hand: Math.max(0, Number(box.on_hand) - cases), updated_at: today() }).eq('id', box.id);
        await supabase.from('inventory_adjustments').insert({ item_type: 'raw', item_id: box.id, item_name: box.name, adjustment: -cases, reason: `Pack-out: ${cases} cases of ${sku.sku}` });
      }
    }

    // 4. Increment finished goods (in cases)
    const { data: fgData } = await supabase.from('finished_goods').select('on_hand').eq('id', form.finished_good_id).single();
    await supabase.from('finished_goods').update({ on_hand: Number(fgData.on_hand) + cases }).eq('id', form.finished_good_id);
    await supabase.from('inventory_adjustments').insert({ item_type: 'finished', item_id: form.finished_good_id, item_name: `${sku.sku} – ${sku.name}`, adjustment: cases, reason: `Pack-out run: ${form.run_date}` });

    setSaving(false);
    setModal(false);
    setPreview(null);
    load();
  }

  // Filter SKUs to only those linked to the selected bulk part
  const filteredSkus = form.bulk_part_id
    ? skus.filter(s => s.bulk_part_id === form.bulk_part_id)
    : skus;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Pack-Out Runs</div>
          <div className="page-sub">Pull bulk PBC/RC into customer-specific cases — deducts bulk parts + boxes, adds to Finished Goods</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY); setPreview(null); setModal(true); }}>+ Log Pack-Out</button>
      </div>

      <div className="tbl-wrap">
        <table>
          <thead><tr><th>Date</th><th>Bulk Part</th><th>Packed SKU</th><th>Cases</th><th>Units/Case</th><th>Units Consumed</th><th>Notes</th></tr></thead>
          <tbody>
            {loading && <tr className="empty-row"><td colSpan={7}>Loading...</td></tr>}
            {!loading && runs.length === 0 && <tr className="empty-row"><td colSpan={7}>No pack-out runs logged yet.</td></tr>}
            {runs.map(r => (
              <tr key={r.id}>
                <td>{r.run_date}</td>
                <td>{r.bulk_parts?.name || r.bulk_part_name}</td>
                <td><span className="badge badge-blue mono">{r.sku}</span> <span className="text-muted" style={{fontSize:12}}>{r.finished_goods?.name}</span></td>
                <td className="mono">{Number(r.cases_produced).toLocaleString()}</td>
                <td className="mono">{Number(r.units_per_case).toLocaleString()}</td>
                <td className="mono">{Number(r.units_consumed).toLocaleString()}</td>
                <td className="text-muted" style={{ fontSize: 12 }}>{r.notes || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title="Log Pack-Out Run" onClose={() => { setModal(false); setPreview(null); }} onSave={save} saveLabel={saving ? 'Saving...' : 'Log Pack-Out'}>
          <div className="form-group">
            <label className="form-label">Run Date</label>
            <input className="form-input" type="date" value={form.run_date} onChange={set('run_date')} />
          </div>
          <div className="form-group">
            <label className="form-label">Bulk Part *</label>
            <select className="form-input" value={form.bulk_part_id} onChange={e => { set('bulk_part_id')(e); setForm(f => ({ ...f, finished_good_id: '' })); }}>
              <option value="">— Select bulk part —</option>
              {bulkParts.map(b => <option key={b.id} value={b.id}>{b.name} ({Number(b.on_hand).toLocaleString()} units on hand)</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Customer SKU *</label>
            <select className="form-input" value={form.finished_good_id} onChange={set('finished_good_id')}>
              <option value="">— Select customer SKU —</option>
              {filteredSkus.map(s => <option key={s.id} value={s.id}>{s.sku} – {s.name} {s.case_qty ? `(${s.case_qty}/case)` : ''}</option>)}
            </select>
            {form.bulk_part_id && filteredSkus.length === 0 && (
              <div className="form-hint" style={{ color: 'var(--warn)' }}>No SKUs linked to this bulk part yet. Edit the SKU in Finished Goods and set its Bulk Part source.</div>
            )}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Cases Produced *</label>
              <input className="form-input mono" type="number" min="1" value={form.cases_produced} onChange={set('cases_produced')} placeholder="0" autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Box (optional)</label>
              <select className="form-input" value={form.box_component_id} onChange={set('box_component_id')}>
                <option value="">— Select box —</option>
                {boxes.map(b => <option key={b.id} value={b.id}>{b.name} ({Number(b.on_hand).toLocaleString()} on hand)</option>)}
              </select>
            </div>
          </div>

          {preview && (
            <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Pack-out preview</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                <span>Bulk units consumed</span>
                <span>
                  <strong style={{ fontFamily: 'var(--mono)', color: preview.bulkShort ? 'var(--danger)' : 'inherit' }}>{preview.unitsNeeded.toLocaleString()}</strong>
                  <span style={{ color: 'var(--muted)', fontSize: 11, marginLeft: 6 }}>({preview.bulkOnHand.toLocaleString()} on hand{preview.bulkShort ? ' — SHORT' : ''})</span>
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                <span>Cases out → Finished Goods</span>
                <strong style={{ fontFamily: 'var(--mono)' }}>{preview.casesProduced.toLocaleString()} cases ({preview.unitsPerCase}/case)</strong>
              </div>
              {preview.boxName && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span>Boxes consumed ({preview.boxName})</span>
                  <span>
                    <strong style={{ fontFamily: 'var(--mono)', color: preview.boxShort ? 'var(--danger)' : 'inherit' }}>{preview.boxesNeeded.toLocaleString()}</strong>
                    <span style={{ color: 'var(--muted)', fontSize: 11, marginLeft: 6 }}>({preview.boxOnHand?.toLocaleString()} on hand{preview.boxShort ? ' — SHORT' : ''})</span>
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Notes (optional)</label>
            <input className="form-input" value={form.notes} onChange={set('notes')} placeholder="e.g. Shift, lot, customer PO#..." />
          </div>
        </Modal>
      )}
    </div>
  );
}
